// =================================================================================
// File: src/pages/visitor/VisitorStudioDetail.jsx
// Description: Studio detail page for visitors showing workshops and bookable spaces
// =================================================================================

import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Users,
  Calendar,
  Clock,
  DollarSign,
  Globe,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import { useWorkshops } from "../../contexts/WorkshopContext";
import { apiFetch } from "../../lib/apiFetch";
import WorkshopBookingModalVisitor from "../../components/Modals/WorkshopBookingModalVisitor";
import SpaceBookingModal from "../../components/Modals/SpaceBookingModal";
import { VisitorSpaceCard, VisitorWorkshopCard } from "../../components/Cards/VisitorStudioCards";

// =================================================================================
// Main Page Component
// =================================================================================

export default function VisitorStudioDetail() {
  const { studioId } = useParams();
  const navigate = useNavigate();

  const { allPublicStudios = [] } = usePublicStudio() || {};
  const workshopContext = useWorkshops();

  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkshop, setSelectedWorkshop] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [workshopModalOpen, setWorkshopModalOpen] = useState(false);
  const [spaceModalOpen, setSpaceModalOpen] = useState(false);
  const [spaces, setSpaces] = useState([]);

  // ---------------------------------------------------------------------------
  // Resolve studio from URL param
  // ---------------------------------------------------------------------------
  const studio = useMemo(() => {
    if (!studioId) return null;
    return (
      allPublicStudios.find(
        (s) =>
          String(s.studio_id) === String(studioId) ||
          String(s.id) === String(studioId) ||
          String(s.slug) === String(studioId) ||
          String(s.user_id) === String(studioId)
      ) || null
    );
  }, [allPublicStudios, studioId]);

  // ---------------------------------------------------------------------------
  // Fetch workshops for this studio from API (visitor side never uses local LS)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    async function fetchWorkshops() {
      if (!studio) return;

      try {
        setLoading(true);

        const fetchStudioWorkshops = async (fromIso) => {
          const apiStudioId = studio.studio_id || studio.id;
          const url = `/api/public/studio/${encodeURIComponent(String(apiStudioId))}/workshops?limit=100&from=${encodeURIComponent(fromIso)}`;
          const res = await apiFetch(url);
          const json = await res.json();
          if (!res.ok) throw new Error(json?.error || "Failed to load workshops");
          const rows = Array.isArray(json) ? json : json?.data;
          return Array.isArray(rows) ? rows : [];
        };

        const upcoming = await fetchStudioWorkshops(new Date().toISOString());
        if (upcoming.length > 0) {
          setWorkshops(upcoming);
          return;
        }

        // Try a wider query if the first one returned nothing
        const fallback = await fetchStudioWorkshops("1970-01-01T00:00:00Z");
        if (fallback.length > 0) {
          setWorkshops(fallback);
          return;
        }

        if (workshopContext) {
          const local = (workshopContext.workshops || []).filter((w) => {
            const isPublished = w.isPublic === true || w.is_published === true;
            const matchesStudio =
              String(w.studioId) === String(studio.id) ||
              String(w.studioId) === String(studio.studio_id) ||
              w.studioSlug === studio.slug;
            return isPublished && matchesStudio;
          });

          const mapped = local.map((w) => ({
            id: w.id,
            title: w.title,
            start_at: w.startISO || w.start_at || null,
            end_at: w.endISO || w.end_at || w.startISO || null,
            capacity: w.capacity || 10,
            seats_taken: 0,
            price_cents: Math.round((w.price || 0) * 100),
            currency: w.currency || "USD",
            skill_level: w.level,
            hero_image_url: w.teaserDataUrl || null,
            studio: {
              name: studio.display_name || studio.name || "Your Studio",
              slug: studio.slug || "",
              city: studio.city || "",
              country: studio.country || "",
            },
          }));

          setWorkshops(mapped);
          return;
        }

        setWorkshops([]);
      } catch (err) {
        console.error("[VisitorStudioDetail] Error fetching workshops:", err);
        setWorkshops([]);
      } finally {
        setLoading(false);
      }
    }

    fetchWorkshops();
  }, [studio, workshopContext]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const studioWorkshops = useMemo(() => workshops, [workshops]);

  const studioSpaces = useMemo(() => {
    if (!studio) return [];
    return spaces;
  }, [spaces, studio]);

  // ---------------------------------------------------------------------------
  // Fetch spaces for visitors (public API, server-side cached)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let alive = true;
    async function fetchSpaces() {
      if (!studio) return;
      try {
        const apiStudioId = studio.studio_id || studio.id;
        const res = await apiFetch(
          `/api/public/studio/${encodeURIComponent(String(apiStudioId))}/spaces`
        );
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.error || "Failed to load spaces");
        }
        const data = Array.isArray(json) ? json : json?.data;
        const error = null;
        if (!alive) return;
        if (error) {
          console.error("[VisitorStudioDetail] Error fetching visitor spaces:", error);
          setSpaces([]);
          return;
        }
        const spacesArray = Array.isArray(data) ? data : [];
        setSpaces(spacesArray);
      } catch (err) {
        if (!alive) return;
        console.error("[VisitorStudioDetail] Error fetching visitor spaces:", err);
        setSpaces([]);
      }
    }
    fetchSpaces();
    return () => {
      alive = false;
    };
  }, [studio]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleBookWorkshop = (workshop) => {
    setSelectedWorkshop(workshop);
    setWorkshopModalOpen(true);
  };

  const handleBookSpace = (space) => {
    if (!space?.id) {
      console.warn("[VisitorStudioDetail] Missing space id, cannot navigate", space);
      return;
    }
    const target = `/discover/bookings/${space.id}`;
    console.log("[VisitorStudioDetail] Book space clicked:", space.name, "→", target);
    navigate(target, {
      state: {
        space,
        studio,
        source: "visitor-studio-detail",
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Not-found state
  // ---------------------------------------------------------------------------
  if (!studio) {
    return (
      <div className="relative w-full min-h-screen overflow-hidden font-sans text-white">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="fixed top-0 left-0 w-full h-full object-cover z-0"
        >
          <source src="/intro.mp4" type="video/mp4" />
        </video>

        <div className="fixed inset-0 z-10 bg-black/40" />

        <div className="relative z-20 min-h-screen flex items-center justify-center">
          <motion.div
            className="mudo-glass-panel p-12 text-center max-w-md mx-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-6xl mb-4">🏺</div>
            <h2 className="text-2xl font-bold mb-3">Studio Not Found</h2>
            <p className="text-white/60 mb-6">
              The studio you're looking for doesn't exist or is not published.
            </p>
            <button
              onClick={() => navigate("/discover/map")}
              className="px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 transition-all"
            >
              Back to Map
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Normal render
  // ---------------------------------------------------------------------------
  return (
    <div className="relative w-full min-h-screen overflow-hidden font-sans text-white">
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="fixed top-0 left-0 w-full h-full object-cover z-0"
      >
        <source src="/intro.mp4" type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className="fixed inset-0 z-10 bg-black/40" />

      {/* Content */}
      <div className="relative z-20 min-h-screen">
        {/* Header */}
        <motion.div
          className="sticky top-0 z-30 bg-black/30 backdrop-blur-md border-b border-white/10"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back</span>
            </button>
            <h1 className="text-2xl font-bold">
              {studio.display_name || studio.name || "Studio"}
            </h1>
            <div className="w-24" />
          </div>
        </motion.div>

        {/* Studio Info Section */}
        <motion.div
          className="max-w-7xl mx-auto px-6 py-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Studio Header Card */}
          <div className="mudo-glass-panel p-8 mb-8">
            <div className="flex items-start gap-6">
              {/* Studio Avatar/Icon */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-3xl flex-shrink-0">
                🏺
              </div>

              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-3">
                  {studio.display_name || studio.name || "Studio"}
                </h2>

                {(studio.city || studio.country) && (
                  <div className="flex items-center gap-2 text-white/70 mb-3">
                    <MapPin className="w-5 h-5" />
                    <span>
                      {[studio.city, studio.country]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}

                {studio.address && (
                  <div className="flex items-center gap-2 text-white/60 mb-3 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{studio.address}</span>
                  </div>
                )}

                {studio.bio && (
                  <p className="text-white/80 leading-relaxed mb-4">
                    {studio.bio}
                  </p>
                )}

                {studio.website && (
                  <a
                    href={studio.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="text-sm">Visit Website</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Workshops Section */}
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold mb-1">Workshops</h3>
                <p className="text-white/60 text-sm">
                  {studioWorkshops.length}{" "}
                  {studioWorkshops.length === 1
                    ? "workshop"
                    : "workshops"}{" "}
                  available
                </p>
              </div>
            </div>

            {loading ? (
              <div className="mudo-glass-panel p-12 text-center">
                <div className="inline-block w-10 h-10 border-4 border-white/40 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-white/70 text-sm">Loading workshops…</p>
              </div>
            ) : studioWorkshops.length === 0 ? (
              <motion.div
                className="mudo-glass-panel p-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-5xl mb-3">📅</div>
                <h4 className="text-lg font-semibold mb-2">
                  No Upcoming Workshops
                </h4>
                <p className="text-white/60 text-sm">
                  This studio doesn't have any workshops scheduled yet.
                </p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnimatePresence mode="popLayout">
                  {studioWorkshops.map((workshop) => (
                    <VisitorWorkshopCard
                      key={workshop.id || workshop.title}
                      workshop={workshop}
                      onBook={() => handleBookWorkshop(workshop)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </section>

          {/* Studio Spaces Section */}
          <section className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold mb-1">Book Studio Space</h3>
                <p className="text-white/60 text-sm">
                  {studioSpaces.length}{" "}
                  {studioSpaces.length === 1 ? "space" : "spaces"} available
                </p>
              </div>
            </div>

            {studioSpaces.length === 0 ? (
              <motion.div
                className="mudo-glass-panel p-12 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-5xl mb-3">🏢</div>
                <h4 className="text-lg font-semibold mb-2">
                  No Bookable Spaces Yet
                </h4>
                <p className="text-white/60 text-sm">
                  This studio doesn't have any spaces available for booking at
                  the moment.
                </p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {studioSpaces.map((space) => (
                    <div
                      key={space.id}
                      onClick={() => console.log("[VisitorStudioDetail] Card wrapper click", space.id)}
                      className="mudo-glass-panel mudo-glass-interactive overflow-hidden group pointer-events-auto relative z-30"
                    >
                      <div
                        className="h-2 w-full"
                        style={{
                          background:
                            space.accentColor || "linear-gradient(90deg, #f97316, #fb923c)",
                        }}
                      />

                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-2 text-white/50">
                            <Clock className="w-4 h-4" />
                            <span className="text-xs font-medium">Studio Space</span>
                          </div>
                          {space.availability?.statusBadge && (
                            <div className="mudo-glass-pill px-3 py-1 text-xs font-medium text-white/90">
                              {space.availability.statusBadge}
                            </div>
                          )}
                        </div>

                        <h4 className="text-xl font-bold mb-2 group-hover:text-white/90 transition-colors">
                          {space.name}
                        </h4>

                        {space.location && (
                          <div className="flex items-center gap-2 text-white/60 mb-4 text-sm">
                            <MapPin className="w-4 h-4" />
                            <span>{space.location}</span>
                          </div>
                        )}

                        {space.description && (
                          <p className="text-white/70 text-sm mb-4 line-clamp-2">
                            {space.description}
                          </p>
                        )}

                        {space.tags && space.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {space.tags.slice(0, 3).map((tag, idx) => (
                              <span
                                key={idx}
                                className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/80"
                              >
                                {tag}
                              </span>
                            ))}
                            {space.tags.length > 3 && (
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/60">
                                +{space.tags.length - 3}
                              </span>
                            )}
                          </div>
                        )}

                        {space.capacity?.seats && (
                          <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                            <Users className="w-4 h-4" />
                            <span>
                              Up to {space.capacity.seats}{" "}
                              {space.capacity.seats === 1 ? "person" : "people"}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-4 mb-6">
                          <DollarSign className="w-5 h-5 text-white/50" />
                          <div className="flex flex-wrap gap-3">
                            {space.pricing?.perHour && (
                              <div>
                                <span className="text-2xl font-bold text-white">
                                  ${space.pricing.perHour}
                                </span>
                                <span className="text-sm text-white/60"> /hour</span>
                              </div>
                            )}
                            {space.pricing?.perDay && (
                              <div>
                                <span className="text-2xl font-bold text-white">
                                  ${space.pricing.perDay}
                                </span>
                                <span className="text-sm text-white/60"> /day</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <Link
                          to={`/discover/bookings/${space.id}`}
                          state={{ space, studio, source: "visitor-studio-detail" }}
                          onClick={(e) => {
                            console.log("[VisitorStudioDetail] Space CTA clicked (Link)", space.id);
                          }}
                          className="block w-full"
                          style={{ textDecoration: "none" }}
                        >
                          <div
                            className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-xl pointer-events-auto cursor-pointer text-center relative z-40"
                            style={{
                              background:
                                space.accentColor || "linear-gradient(135deg, #f97316, #fb923c)",
                            }}
                          >
                            View Availability &amp; Book
                          </div>
                        </Link>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </section>
        </motion.div>
      </div>

      {/* Workshop Booking Modal */}
      {selectedWorkshop && (
        <WorkshopBookingModalVisitor
          isOpen={workshopModalOpen}
          onClose={() => {
            setWorkshopModalOpen(false);
            setSelectedWorkshop(null);
          }}
          workshop={selectedWorkshop}
        />
      )}

      {/* Space Booking Modal - Disabled for now (requires visitor-specific booking flow) */}
      {/* 
      {selectedSpace && (
        <SpaceBookingModal
          isOpen={spaceModalOpen}
          onClose={() => {
            setSpaceModalOpen(false);
            setSelectedSpace(null);
          }}
          space={selectedSpace}
          visitorMode={true}
        />
      )}
      */}
    </div>
  );
}

// =================================================================================
// WorkshopCard Component
// =================================================================================

const WorkshopCard = React.forwardRef(({ workshop, onBook }, ref) => {
  const {
    title,
    start_at,
    end_at,
    capacity,
    seats_taken = 0,
    price_cents,
    currency = "USD",
    skill_level,
    hero_image_url,
  } = workshop;

  const seatsAvailable = (capacity || 0) - (seats_taken || 0);
  const price = price_cents ? price_cents / 100 : 0;
  const startDate = start_at ? new Date(start_at) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="mudo-glass-panel mudo-glass-interactive overflow-hidden group"
      ref={ref}
    >
      <div className="h-2 w-full bg-gradient-to-r from-purple-500 to-pink-500" />

      {hero_image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={hero_image_url}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 text-white/50">
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-medium">Workshop</span>
          </div>
          {skill_level && (
            <div className="mudo-glass-pill px-3 py-1 text-xs font-medium text-white/90">
              {skill_level}
            </div>
          )}
        </div>

        <h4 className="text-xl font-bold mb-3 group-hover:text-white/90 transition-colors">
          {title}
        </h4>

        {startDate && (
          <div className="flex items-center gap-2 text-white/60 mb-3 text-sm">
            <Clock className="w-4 h-4" />
            <span>{startDate.toLocaleString()}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
          <Users className="w-4 h-4" />
          <span>
            {seatsAvailable > 0
              ? `${seatsAvailable} spot${
                  seatsAvailable === 1 ? "" : "s"
                } available`
              : "Fully booked"}
          </span>
        </div>

        {price > 0 && (
          <div className="flex items-center gap-2 mb-6">
            <DollarSign className="w-5 h-5 text-white/50" />
            <div>
              <span className="text-2xl font-bold text-white">
                {currency} {price.toFixed(2)}
              </span>
              <span className="text-sm text-white/60"> per person</span>
            </div>
          </div>
        )}

        <button
          onClick={onBook}
          disabled={seatsAvailable <= 0}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              seatsAvailable > 0
                ? "linear-gradient(135deg, #a855f7, #ec4899)"
                : "linear-gradient(135deg, #6b7280, #4b5563)",
          }}
        >
          {seatsAvailable > 0 ? "View & Book" : "Fully Booked"}
        </button>
      </div>
    </motion.div>
  );
});
WorkshopCard.displayName = 'WorkshopCard';

// =================================================================================
// SpaceCard Component
// =================================================================================

const RawSpaceCard = ({ space, onBook }) => {
  const {
    name,
    location,
    description,
    tags = [],
    capacity = {},
    pricing = {},
    availability = {},
    accentColor,
  } = space;

  return (
    <div className="mudo-glass-panel mudo-glass-interactive overflow-hidden group">
      <div
        className="h-2 w-full"
        style={{
          background:
            accentColor || "linear-gradient(90deg, #f97316, #fb923c)",
        }}
      />

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 text-white/50">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Studio Space</span>
          </div>
          {availability.statusBadge && (
            <div className="mudo-glass-pill px-3 py-1 text-xs font-medium text-white/90">
              {availability.statusBadge}
            </div>
          )}
        </div>

        <h4 className="text-xl font-bold mb-2 group-hover:text-white/90 transition-colors">
          {name}
        </h4>

        {location && (
          <div className="flex items-center gap-2 text-white/60 mb-4 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{location}</span>
          </div>
        )}

        {description && (
          <p className="text-white/70 text-sm mb-4 line-clamp-2">
            {description}
          </p>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/80"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/60">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {capacity.seats && (
          <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
            <Users className="w-4 h-4" />
            <span>
              Up to {capacity.seats}{" "}
              {capacity.seats === 1 ? "person" : "people"}
            </span>
          </div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <DollarSign className="w-5 h-5 text-white/50" />
          <div className="flex flex-wrap gap-3">
            {pricing.perHour && (
              <div>
                <span className="text-2xl font-bold text-white">
                  ${pricing.perHour}
                </span>
                <span className="text-sm text-white/60"> /hour</span>
              </div>
            )}
            {pricing.perDay && (
              <div>
                <span className="text-2xl font-bold text-white">
                  ${pricing.perDay}
                </span>
                <span className="text-sm text-white/60"> /day</span>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            console.log("[SpaceCard] Button clicked");
            onBook();
          }}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
          style={{
            background:
              accentColor || "linear-gradient(135deg, #f97316, #fb923c)",
          }}
        >
          View Availability &amp; Book
        </button>
      </div>
    </div>
  );
};

const SpaceCard = motion.create(RawSpaceCard);
